import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { createLightTheme, createDarkTheme } from '@fluentui/react-components';

const brand = {
    10: '#210d05', 20: '#371709', 30: '#51220b', 40: '#692e0f',
    50: '#803a12', 60: '#944617', 70: '#a4511d', 80: '#b65b23',
    90: '#c76b31', 100: '#d77e46', 110: '#e39260', 120: '#eca87e',
    130: '#f2bea0', 140: '#f7d5c0', 150: '#fbe8dc', 160: '#fdf5ef',
};
const shape = { borderRadiusSmall: '2px', borderRadiusMedium: '3px', borderRadiusLarge: '4px', borderRadiusXLarge: '6px' };
const lightTheme = {
    ...createLightTheme(brand), ...shape,
    colorNeutralBackground1: '#fffefa', colorNeutralBackground2: '#f8f7f3',
    colorNeutralBackground3: '#f1f0eb', colorNeutralStroke2: '#deded7',
};
const darkTheme = { ...createDarkTheme(brand), ...shape };

const STORAGE_KEY = 'ezfunc-theme-mode';
const MODES = ['light', 'dark', 'system'];

const ThemeContext = createContext({
    mode: 'dark',
    resolvedMode: 'dark',
    theme: darkTheme,
    setMode: () => { },
    cycleMode: () => { },
});

function getSystemMode() {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
}

function readStoredMode() {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return MODES.includes(stored) ? stored : 'system';
    } catch (error) {
        console.warn('Theme preference could not be read:', error);
        return 'system';
    }
}

export function ThemeProvider({ children }) {
    const [mode, setModeState] = useState(readStoredMode);
    const [systemMode, setSystemMode] = useState(getSystemMode);

    // Track OS preference so 'system' stays live.
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-color-scheme: light)');
        const handler = () => setSystemMode(mq.matches ? 'light' : 'dark');
        mq.addEventListener?.('change', handler);
        return () => mq.removeEventListener?.('change', handler);
    }, []);

    const resolvedMode = mode === 'system' ? systemMode : mode;
    const theme = resolvedMode === 'light' ? lightTheme : darkTheme;

    const setMode = useCallback((next) => {
        if (!MODES.includes(next)) throw new Error(`Unsupported theme: ${next}`);
        setModeState(next);
    }, []);

    const cycleMode = useCallback(() => {
        setModeState((prev) => {
            const next = MODES[(MODES.indexOf(prev) + 1) % MODES.length];
            return next;
        });
    }, []);

    useEffect(() => {
        try { window.localStorage.setItem(STORAGE_KEY, mode); }
        catch (error) { console.warn('Theme preference could not be saved:', error); }
    }, [mode]);

    // Keep the document surface (outside the FluentProvider) in sync with the theme.
    useEffect(() => {
        document.body.style.backgroundColor = theme.colorNeutralBackground1;
        document.body.style.color = theme.colorNeutralForeground1;
        document.body.dataset.theme = resolvedMode;
        document.documentElement.style.colorScheme = resolvedMode;
    }, [theme, resolvedMode]);

    const value = useMemo(
        () => ({ mode, resolvedMode, theme, setMode, cycleMode }),
        [mode, resolvedMode, theme, setMode, cycleMode]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeMode() {
    return useContext(ThemeContext);
}
